import React, { useState, useEffect, useMemo, useRef } from "react";
import { useParams } from "react-router-dom";
import { Box, Paper, Typography, Button, CircularProgress, Container, Backdrop, Chip, Divider, Card, CardContent, CardHeader, CardActions, Collapse, IconButton, Badge, Alert, useMediaQuery, Accordion, AccordionSummary, AccordionDetails, Tooltip, TextField, Grid, Avatar, LinearProgress, Stack,  } from '@mui/material';
import { styled, useTheme, alpha } from '@mui/material/styles';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  AttachMoney as AttachMoneyIcon,
  Assignment as AssignmentIcon,
  AssignmentTurnedIn as AssignmentTurnedInIcon,
  Timeline as TimelineIcon,
  Info as InfoIcon,
  Link as LinkIcon,
  Save as SaveIcon,
  Upload as UploadIcon,
  CheckCircle as CheckCircleIcon,
  HelpOutline as HelpOutlineIcon,
  MovieCreation as MovieCreationIcon,
  Code as CodeIcon,
  Launch as LaunchIcon,
  CloudUpload as CloudUploadIcon,
  VideoLibrary as VideoLibraryIcon,
  TrendingUp as TrendingUpIcon,
  Schedule as ScheduleIcon,
  RadioButtonUnchecked as RadioButtonUncheckedIcon,
  Warning as WarningIcon,
  Edit as EditIcon,
  Chat as ChatIcon,
  Close as CloseIcon,
  Description as DescriptionIcon,
} from "@mui/icons-material";
import { useQuery, useMutation } from "react-query";
import client from "../../../../API";
import { useCreatorAuth } from "../../../../Hooks/creator-use-auth";
import CreatorTimeline from "./creatorTimeline";
import useAlertDialog from "../../../../Components/useAlertDialog";
import AlertDialog from "../../../../Components/AlertDialog";
import CampaignChatScreen from "../../../../Components/ChatComponents/CampaignChatScreen";
import {
  formatMessageContent,
  TimelineStatus,
} from "../../../../Utils/constants";
import PageTitle from "../../../../Components/PageTitle";

const MOBILE_HEADER_HEIGHT = 56;

function formatIdealDueDate(dateString) {
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return "Invalid Date";
  const month = (date.getMonth() + 1).toString().padStart(2, "0");
  const day = date.getDate().toString().padStart(2, "0");
  const year = date.getFullYear().toString().substr(-2);
  return `${month}/${day}/${year}`;
}

const getStatusTooltip = (status) => {
  const statusTooltips = {
    "Draft": "Campaign is being prepared - not yet launched",
    "Launched": "Campaign is live and active - you can interact with the brand",
    "Completed": "Campaign has been finished and finalized",
    "Pitched": "Initial outreach sent - waiting for your response",
    "Counter": "You have made a counter-offer - waiting for brand response",
    "Accepted": "You have accepted the campaign terms and pricing",
    "Approved": "Your content has been approved - campaign deliverables complete",
    "Declined": "You have declined to participate in this campaign",
    "Dropped": "You have been removed from the campaign",
    "Pending": "Waiting for action or approval",
  };
  return statusTooltips[status] || `Current status: ${status}`;
};

const calculateVideoProgress = (videoData, crosspostData = []) => {
  const steps = [
    { id: 'created', label: 'Assignment', description: 'Video assignment created', completed: true },
    { id: 'submission', label: 'Content Submitted', description: 'Draft/content uploaded', completed: !!(videoData.submissionLink || videoData.draftLink) },
    { id: 'live', label: 'Live Link', description: 'Published content link added', completed: !!videoData.liveLink },
    { id: 'boost', label: 'Boost Code', description: 'Promotional code provided', completed: !!videoData.boostCode },
    { id: 'crossposts', label: 'Crossposts', description: 'All crosspost links completed', completed: crosspostData.length === 0 || crosspostData.every(c => c.liveLink && c.boostCode) },
    { id: 'payment', label: 'Payment', description: 'Payment processed', completed: !!videoData.payoutComplete },
  ];
  const completedSteps = steps.filter(step => step.completed);
  const progressPercentage = (completedSteps.length / steps.length) * 100;
  return {
    steps,
    completedSteps: completedSteps.length,
    totalSteps: steps.length,
    progressPercentage,
    status: progressPercentage === 100 ? 'completed' : progressPercentage > 0 ? 'in-progress' : 'not-started',
    nextStep: steps.find(step => !step.completed)?.label || null
  };
};

/*
  KEY FIX: height:"100%" instead of height:"100vh" / calc(100vh - ...).
  This component lives inside AgencyRoot's Outlet box which is flex:1
  with a known height. Using 100vh here overcounts by the height of the
  MUI Toolbar + the agency tab bar, creating overflow and a visible gap.
  height:"100%" fills exactly the space the parent has allocated.
*/
const MainContainer = styled(Container)(({ theme }) => ({
  padding: 0,
  height: "100%",
  display: "flex",
  flexDirection: "column",
  maxWidth: "none !important",
  overflow: "hidden",
  [theme.breakpoints.up("lg")]: {
    padding: theme.spacing(2),
  },
}));

const DesktopChatContainer = styled(Box)(({ theme }) => ({
  display: "none",
  [theme.breakpoints.up("lg")]: {
    display: "flex",
    height: "100%",
    gap: theme.spacing(2),
    overflow: "hidden",
  },
}));

const MobileChatContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  height: "100%",
  overflow: "hidden",
  position: "relative",
  [theme.breakpoints.up("lg")]: {
    display: "none",
  },
}));

const DesktopSidebar = styled(Card)(({ theme }) => ({
  minWidth: 380,
  maxWidth: 420,
  height: "100%",
  overflowY: "auto",
  display: "flex",
  flexDirection: "column",
}));

const DesktopChatCard = styled(Card)(({ theme }) => ({
  flex: 1,
  display: "flex",
  flexDirection: "column",
  height: "100%",
  overflow: "hidden",
  minWidth: 0,
}));

const MobileCampaignAccordion = styled(Accordion)(({ theme }) => ({
  margin: 0,
  boxShadow: "none",
  borderBottom: `1px solid ${theme.palette.divider}`,
  "&:before": { display: "none" },
  "&.Mui-expanded": { margin: 0 },
  '&.chat-mode-hidden': { display: 'none' }
}));

const MobileScrollableContent = styled(Box)(({ theme }) => ({
  flex: 1,
  overflowY: "auto",
  overflowX: "hidden",
  WebkitOverflowScrolling: "touch",
  position: "relative",
  '&::-webkit-scrollbar': { width: 4 },
  '&::-webkit-scrollbar-track': { backgroundColor: 'transparent' },
  '&::-webkit-scrollbar-thumb': { backgroundColor: alpha(theme.palette.text.primary, 0.2), borderRadius: 2 },
}));

const MobileFullscreenChat = styled(Box)(({ theme }) => ({
  position: "fixed",
  top: 0, left: 0, right: 0, bottom: 0,
  backgroundColor: theme.palette.background.default,
  zIndex: theme.zIndex.modal - 1,
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
}));

const MobileChatHeader = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: theme.spacing(1.5, 2),
  borderBottom: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.paper,
  minHeight: MOBILE_HEADER_HEIGHT,
  flexShrink: 0,
}));

const InfoSection = styled(Box)(({ theme, compact = false }) => ({
  padding: compact ? theme.spacing(1.5) : theme.spacing(2),
  borderBottom: `1px solid ${theme.palette.divider}`,
  "&:last-child": { borderBottom: "none" },
  "& + &": { borderTop: "none" },
}));

const CompactSection = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1, 0),
  "&:not(:last-child)": {
    borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
    marginBottom: theme.spacing(1),
    paddingBottom: theme.spacing(1),
  },
}));

const ChatContentWrapper = styled(Box)({
  flex: 1,
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
  minHeight: 0,
});

const CampaignBriefCard = styled(Card)(({ theme }) => ({
  margin: theme.spacing(2, 2, 0, 2),
  borderRadius: theme.spacing(2),
  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
  background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.02)}, ${alpha(theme.palette.secondary.main, 0.02)})`,
  transition: 'all 0.3s ease',
  '&:hover': { boxShadow: theme.shadows[4] },
  [theme.breakpoints.up("lg")]: { margin: 0, marginBottom: theme.spacing(2) }
}));

const DeliverableVideoCard = ({
  videoData, crosspostData = [], onLinkChange, onCrossLinkChange,
  onSubmissionLinkChange, onSave, isSaving, hasUnsavedChanges, canEdit, theme, creator
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const progress = calculateVideoProgress(videoData, crosspostData);

  const getStatusColor = () => {
    switch (progress.status) {
      case 'completed': return theme.palette.success.main;
      case 'in-progress': return theme.palette.primary.main;
      default: return theme.palette.grey[400];
    }
  };

  const getStatusIcon = (completed, isActive = false) => {
    if (completed) return <CheckCircleIcon sx={{ fontSize: 16, color: 'success.main' }} />;
    if (isActive) return <ScheduleIcon sx={{ fontSize: 16, color: 'primary.main' }} />;
    return <RadioButtonUncheckedIcon sx={{ fontSize: 16, color: 'grey.400' }} />;
  };

  return (
    <Card elevation={1} sx={{ mb: 1.5, border: `1px solid ${alpha(getStatusColor(), 0.2)}`, borderRadius: 2, overflow: 'hidden', transition: 'all 0.2s ease', '&:hover': { boxShadow: theme.shadows[2], borderColor: alpha(getStatusColor(), 0.4) } }}>
      <Box sx={{ p: 1.5, bgcolor: alpha(getStatusColor(), 0.03), borderBottom: isExpanded ? `1px solid ${theme.palette.divider}` : 'none' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Avatar sx={{ width: 28, height: 28, bgcolor: getStatusColor(), fontSize: '0.8rem' }}>{String(videoData.id).padStart(2, "0")}</Avatar>
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, lineHeight: 1.2 }}>{videoData.promotionPlatform}</Typography>
              <Typography variant="caption" color="text.secondary">{videoData.promotionType}</Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip label={`${Math.round(progress.progressPercentage)}%`} size="small" color={progress.status === 'completed' ? 'success' : progress.status === 'in-progress' ? 'primary' : 'default'} sx={{ height: 20, fontSize: '0.65rem', minWidth: 40 }} />
            <IconButton size="small" onClick={() => setIsExpanded(!isExpanded)} sx={{ width: 24, height: 24 }}>
              <ExpandMoreIcon sx={{ fontSize: 16, transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }} />
            </IconButton>
          </Box>
        </Box>
        <LinearProgress variant="determinate" value={progress.progressPercentage} sx={{ height: 4, borderRadius: 2, backgroundColor: alpha(theme.palette.grey[300], 0.3), '& .MuiLinearProgress-bar': { backgroundColor: getStatusColor(), borderRadius: 2 } }} />
        {!isExpanded && (
          <Box sx={{ display: 'flex', gap: 0.5, mt: 1, flexWrap: 'wrap' }}>
            {progress.steps.slice(0, 4).map((step) => (
              <Chip key={step.id} size="small" label={step.label.split(' ')[0]} variant={step.completed ? "filled" : "outlined"} color={step.completed ? "success" : "default"} sx={{ height: 18, fontSize: '0.6rem', '& .MuiChip-label': { px: 0.5 } }} />
            ))}
          </Box>
        )}
      </Box>

      <Collapse in={isExpanded}>
        <Box sx={{ p: 1.5 }}>
          {canEdit && (
            <CompactSection>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: 'primary.main' }}>📤 Content Submission</Typography>
              <TextField fullWidth size="small" label="Draft/Submission Link (System Generated)" value={creator?.submissionLink || ""} disabled placeholder="Upload folder will be assigned" sx={{ mb: 1, '& .MuiInputBase-input.Mui-disabled': { WebkitTextFillColor: theme.palette.text.secondary } }} helperText="This link is automatically set. Only change if instructed by the brand." />
              {creator?.submissionLink && (
                <Button fullWidth variant="outlined" size="small" startIcon={<CloudUploadIcon />} onClick={() => window.open(creator.submissionLink, "_blank")} sx={{ mt: 0.5 }}>Upload Files to Google Drive</Button>
              )}
            </CompactSection>
          )}
          {canEdit && (
            <CompactSection>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: 'primary.main' }}>🔗 Published Content</Typography>
              <Stack spacing={1}>
                <TextField fullWidth size="small" label="Live Link" value={videoData.liveLink || ""} onChange={(e) => onLinkChange?.(videoData.id, e.target.value, videoData.boostCode || "")} placeholder="Link to published content" InputProps={{ endAdornment: videoData.liveLink && (<IconButton size="small" onClick={() => window.open(videoData.liveLink, '_blank')}><LaunchIcon fontSize="small" /></IconButton>) }} />
                <TextField fullWidth size="small" label="Boost/Promo Code" value={videoData.boostCode || ""} onChange={(e) => onLinkChange?.(videoData.id, videoData.liveLink || "", e.target.value)} placeholder="Optional promotional code" />
              </Stack>
            </CompactSection>
          )}
          {crosspostData.length > 0 && (
            <CompactSection>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: 'secondary.main' }}>📱 Crossposts ({crosspostData.length})</Typography>
              <Stack spacing={1}>
                {crosspostData.map((crosspost) => (
                  <Card key={crosspost.id} variant="outlined" sx={{ p: 1, bgcolor: 'grey.50' }}>
                    <Typography variant="caption" color="primary" sx={{ fontWeight: 600, mb: 0.5, display: 'block' }}>{crosspost.promotionPlatform} • {crosspost.promotionType}</Typography>
                    <Stack spacing={0.5}>
                      <TextField fullWidth size="small" label="Crosspost Link" value={crosspost.liveLink || ""} onChange={(e) => onCrossLinkChange?.(videoData.id, crosspost.id, e.target.value, crosspost.boostCode || "")} disabled={!canEdit} InputProps={{ endAdornment: crosspost.liveLink && (<IconButton size="small" onClick={() => window.open(crosspost.liveLink, '_blank')}><LaunchIcon fontSize="small" /></IconButton>) }} />
                      <TextField fullWidth size="small" label="Boost Code" value={crosspost.boostCode || ""} onChange={(e) => onCrossLinkChange?.(videoData.id, crosspost.id, crosspost.liveLink || "", e.target.value)} disabled={!canEdit} />
                    </Stack>
                  </Card>
                ))}
              </Stack>
            </CompactSection>
          )}
          {canEdit && hasUnsavedChanges && (
            <Box sx={{ mt: 1.5, pt: 1.5, borderTop: `1px solid ${theme.palette.divider}` }}>
              <Button variant="contained" fullWidth size="medium" onClick={onSave} disabled={isSaving} startIcon={isSaving ? <CircularProgress size={16} /> : <SaveIcon />} color="primary">
                {isSaving ? "Saving Changes..." : "Save This Video"}
              </Button>
            </Box>
          )}
          <Box sx={{ mt: 1, p: 1, bgcolor: alpha(theme.palette.grey[100], 0.5), borderRadius: 1 }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>Progress Details:</Typography>
            <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>
              {progress.steps.map((step) => (
                <Tooltip key={step.id} title={step.description}>
                  <Chip size="small" icon={getStatusIcon(step.completed)} label={step.label} variant={step.completed ? "filled" : "outlined"} color={step.completed ? "success" : "default"} sx={{ fontSize: '0.65rem', height: 20 }} />
                </Tooltip>
              ))}
            </Box>
          </Box>
        </Box>
      </Collapse>
    </Card>
  );
};

const CreatorSpecificCampaign = () => {
  const { campaignId } = useParams();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("lg"));

  const [campaign, setCampaign] = useState(null);
  const [timelineEvents, setTimelineEvents] = useState(undefined);
  const editedEvents = useRef(false);
  const [isLoading, setIsLoading] = useState(true);
  const [expandedSections, setExpandedSections] = useState({ brief: false, deliverables: false, videos: true });
  const [mobileActiveAccordion, setMobileActiveAccordion] = useState('details');
  const [mobileChatFullscreen, setMobileChatFullscreen] = useState(false);
  const [ineligibleCount, setIneligibleCount] = useState(0);
  const [liveLink, setLiveLink] = useState([]);
  const [hasUnsavedLinks, setHasUnsavedLinks] = useState(false);
  const [unsavedVideoIds, setUnsavedVideoIds] = useState(new Set());

  const { creatorToken } = useCreatorAuth();
  const { dialogState, openDialog: openAlert, closeDialog } = useAlertDialog();
  const chatRef = useRef(null);

  useEffect(() => {
    const fetchCampaignData = async () => {
      try {
        const data = await client.campaigns.fetch(campaignId);
        let eventData = { events: [] };
        try {
          eventData = await client.campaigns.timeline_by_creator(campaignId, creatorToken.creator_user.username);
        } catch (err) {
          if (err.response && err.response.status === 404) console.warn("No timeline found for this campaign.");
          else throw err;
        }
        editedEvents.current = false;
        setCampaign(data);
        setTimelineEvents(eventData.events.map((event) => ({ ...event, projected_date: new Date(event.projected_date), ogStatus: event.status })));
        setIsLoading(false);
      } catch (err) {
        console.error("Error fetching campaign data:", err);
      }
    };
    fetchCampaignData();
  }, [campaignId, creatorToken.creator_user.username]);

  const { data: checkResults, isLoading: isChecking, error: conversationError } = useQuery({
    queryKey: ["messagesId", campaignId],
    queryFn: () => client.conversations.checkCampaign({ campaign_id: campaignId, creator_id: creatorToken.creator_user.username }),
    refetchOnWindowFocus: false,
    enabled: !!campaignId && !!creatorToken.creator_user.username,
  });

  const convId = checkResults?.exists ? checkResults.conversation_id : null;

  const OnEditTimelineField = (targetIndex, target, value) => {
    editedEvents.current = true;
    setTimelineEvents((prev) => prev.map((event, i) => i === targetIndex ? { ...event, [target]: value } : event));
  };

  const { mutate: updateTimeline, isLoading: isUpdatingTimeline } = useMutation(
    ({ input }) => client.creatorConnect.campaignUpdateTimeline(campaignId, creatorToken.creator_user.username, input),
    {
      onSuccess: () => { openAlert("Success", "Timeline Updated", closeDialog, null, "Ok", null); editedEvents.current = false; },
      onError: (error) => openAlert("Error", `Error when updating timeline: ${error.response?.data?.error || error.message}`, closeDialog, null, "Ok", null),
    }
  );

  const onSaveChanges = () => {
    if (editedEvents.current) {
      const foundSetComplete = timelineEvents.some((event) => event.ogStatus !== event.status && event.status === TimelineStatus[2]);
      if (foundSetComplete) {
        openAlert("Completed Objective", "You're setting an objective as complete. This will notify the brand and lock further changes for that item. Continue?", () => { closeDialog(); updateTimeline({ input: timelineEvents }); }, closeDialog, "Continue", "Cancel");
      } else {
        updateTimeline({ input: timelineEvents });
      }
    }
  };

  const creator = useMemo(() => {
    if (!campaign) return undefined;
    return campaign.creators.find((c) => c.id === creatorToken.creator_user.username);
  }, [campaign, creatorToken.creator_user.username]);

  useEffect(() => {
    if (creator) {
      const newLiveLinks = creator.videos.filter((video) => video.parentId == null).map((video) => ({
        id: video.id, promotionType: video.promotionType, promotionPlatform: video.promotionPlatform,
        liveLink: video.liveLink || "", boostCode: video.boostCode || "",
        submissionLink: video.submissionLink || "", draftLink: video.draftLink || "",
        payoutComplete: video.payoutComplete,
        crossposts: creator.videos.filter((cross) => cross.parentId === video.id).map((cross) => ({
          id: cross.id, promotionType: cross.promotionType, promotionPlatform: cross.promotionPlatform,
          liveLink: cross.liveLink || "", boostCode: cross.boostCode || "",
        })),
      }));
      setLiveLink(newLiveLinks);
      setHasUnsavedLinks(false);
      setUnsavedVideoIds(new Set());
    }
  }, [creator]);

  const organizedVideos = useMemo(() => liveLink.map(video => ({ ...video, crossposts: video.crossposts })), [liveLink]);

  const overallProgress = useMemo(() => {
    if (!organizedVideos.length) return { progressPercentage: 0, completedVideos: 0, totalVideos: 0 };
    let totalSteps = 0, completedSteps = 0;
    organizedVideos.forEach(video => {
      const p = calculateVideoProgress(video, video.crossposts);
      totalSteps += p.totalSteps; completedSteps += p.completedSteps;
    });
    const progressPercentage = totalSteps > 0 ? (completedSteps / totalSteps) * 100 : 0;
    const completedVideos = organizedVideos.filter(v => calculateVideoProgress(v, v.crossposts).status === 'completed').length;
    return { progressPercentage, completedVideos, totalVideos: organizedVideos.length, status: progressPercentage === 100 ? 'completed' : progressPercentage > 0 ? 'in-progress' : 'not-started' };
  }, [organizedVideos]);

  const canEdit = campaign?.campaign_status === "Launched" && creator && !["Declined", "Dropped", "Pitched", "Counter"].includes(creator.status);
  const handleExpandToggle = (section) => setExpandedSections((prev) => ({ ...prev, [section]: !prev[section] }));

  const { mutate: updateFields, isLoading: isUpdatingFields } = useMutation(
    (input) => client.creatorConnect.updateCreatorFields(campaignId, input),
    {
      onSuccess: () => { openAlert("Success", "Fields updated successfully!", closeDialog, null, "Ok", null); setHasUnsavedLinks(false); setUnsavedVideoIds(new Set()); },
      onError: (error) => openAlert("Error", `Error when updating fields: ${error.response?.data?.error || error.message}`, closeDialog, null, "Ok", null),
    }
  );

  const onLinkChange = (videoId, newLiveLink, newBoostCode) => {
    setLiveLink((prev) => prev.map((vid) => vid.id === videoId ? { ...vid, liveLink: newLiveLink, boostCode: newBoostCode } : vid));
    setHasUnsavedLinks(true);
    setUnsavedVideoIds(prev => new Set(prev).add(videoId));
  };

  const onCrossLinkChange = (videoId, crossId, newLiveLink, newBoostCode) => {
    setLiveLink((prev) => prev.map((vid) => vid.id === videoId ? { ...vid, crossposts: vid.crossposts.map((c) => c.id === crossId ? { ...c, liveLink: newLiveLink, boostCode: newBoostCode } : c) } : vid));
    setHasUnsavedLinks(true);
    setUnsavedVideoIds(prev => new Set(prev).add(videoId));
  };

  const onSubmissionLinkChange = (videoId, newSubmissionLink) => {
    setLiveLink((prev) => prev.map((vid) => vid.id === videoId ? { ...vid, submissionLink: newSubmissionLink } : vid));
    setHasUnsavedLinks(true);
    setUnsavedVideoIds(prev => new Set(prev).add(videoId));
  };

  const handleSaveSingleVideo = (videoId) => {
    const video = liveLink.find(v => v.id === videoId);
    if (!video) return;
    updateFields({ liveLinks: [{ id: video.id, liveLink: video.liveLink || null, boostCode: video.boostCode || null, submissionLink: video.submissionLink || null }, ...video.crossposts.map((cross) => ({ id: cross.id, liveLink: cross.liveLink || null, boostCode: cross.boostCode || null }))] });
  };

  const handleAssetsSent = () => updateFields({ assetsSent: true });

  // iOS VH fix — only needed for the fullscreen mobile chat overlay which uses fixed positioning
  useEffect(() => {
    if (isMobile) {
      const setVH = () => { const vh = window.innerHeight * 0.01; document.documentElement.style.setProperty('--vh', `${vh}px`); };
      setVH();
      window.addEventListener('resize', setVH);
      window.addEventListener('orientationchange', setVH);
      return () => { window.removeEventListener('resize', setVH); window.removeEventListener('orientationchange', setVH); };
    }
  }, [isMobile]);

  const handleMobileChatToggle = (open) => {
    setMobileChatFullscreen(open);
    if (open) { document.body.style.overflow = 'hidden'; setMobileActiveAccordion('chat'); }
    else { document.body.style.overflow = ''; setMobileActiveAccordion('details'); }
  };

  const handleMobileAccordionChange = (panel) => {
    if (panel === 'chat') handleMobileChatToggle(true);
    else if (panel === 'details') {
      if (mobileActiveAccordion === 'details') handleMobileChatToggle(true);
      else setMobileActiveAccordion('details');
    }
  };

  useEffect(() => { return () => { document.body.style.overflow = ''; }; }, []);

  const renderCampaignInfo = () => (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <InfoSection>
        <Box sx={{ textAlign: 'center', mb: 2 }}>
          <Typography variant="h3" sx={{ fontWeight: 800, color: 'primary.main', mb: 0.5, textShadow: `0 2px 4px ${alpha(theme.palette.primary.main, 0.2)}` }}>
            {creator?.agencyRate ? `$${creator.agencyRate}` : creator?.price ? `$${creator.price}` : "TBD"}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>Campaign Value</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', flexWrap: 'wrap', mb: 2 }}>
          <Tooltip title={getStatusTooltip(campaign?.campaign_status)} arrow>
            <Chip label={campaign?.campaign_status} color={campaign?.campaign_status === "Launched" ? "success" : "default"} sx={{ cursor: 'help', fontWeight: 600 }} />
          </Tooltip>
          {creator?.status && (
            <Tooltip title={getStatusTooltip(creator.status)} arrow>
              <Chip label={creator.status} color={creator.status === "Accepted" ? "success" : "primary"} variant="outlined" sx={{ cursor: 'help' }} />
            </Tooltip>
          )}
        </Box>
        <Typography variant="h6" sx={{ textAlign: 'center', fontWeight: 600, color: 'text.primary', lineHeight: 1.3 }}>{campaign?.name}</Typography>
        {creator?.assetsSent && (
          <Box sx={{ mt: 2, textAlign: 'center' }}>
            <Chip icon={<CheckCircleIcon />} label="Assets Received ✓" color="success" variant="outlined" sx={{ fontWeight: 600 }} />
          </Box>
        )}
      </InfoSection>

      <CampaignBriefCard elevation={0}>
        <Accordion expanded={expandedSections.brief} onChange={() => handleExpandToggle('brief')} sx={{ boxShadow: 'none', '&:before': { display: 'none' } }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 48, '&.Mui-expanded': { minHeight: 48 }, px: 2, py: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <DescriptionIcon color="primary" />
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Campaign Brief</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0, px: 2, pb: 2 }}>
            <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", color: "text.secondary", lineHeight: 1.6, maxHeight: 300, overflow: "auto", '&::-webkit-scrollbar': { width: 4 }, '&::-webkit-scrollbar-track': { backgroundColor: 'transparent' }, '&::-webkit-scrollbar-thumb': { backgroundColor: alpha(theme.palette.text.primary, 0.2), borderRadius: 2 } }}>
              {formatMessageContent(campaign?.brief || "No brief provided")}
            </Typography>
          </AccordionDetails>
        </Accordion>
      </CampaignBriefCard>

      <Box sx={{ mt: 2 }}>
        <InfoSection sx={{ borderBottom: 'none' }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
              <AssignmentIcon color="primary" />Deliverables
            </Typography>
            {ineligibleCount > 0 && (
              <Tooltip title="Pending deliverables - click to view in chat" arrow>
                <IconButton color="error" size="small" onClick={() => { if (isMobile) handleMobileChatToggle(true); setTimeout(() => chatRef.current?.scrollToFirstDeliverable?.(), 100); }} sx={{ bgcolor: alpha(theme.palette.error.main, 0.1), '&:hover': { bgcolor: alpha(theme.palette.error.main, 0.2) } }}>
                  <Badge badgeContent={ineligibleCount} color="error"><WarningIcon /></Badge>
                </IconButton>
              </Tooltip>
            )}
          </Box>

          {organizedVideos.length > 0 && (
            <Box sx={{ mb: 2, p: 1.5, bgcolor: alpha(theme.palette.primary.main, 0.05), borderRadius: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Overall Progress</Typography>
                <Typography variant="caption" color="primary" sx={{ fontWeight: 600 }}>{overallProgress.completedVideos}/{overallProgress.totalVideos} Complete</Typography>
              </Box>
              <LinearProgress variant="determinate" value={overallProgress.progressPercentage} sx={{ height: 8, borderRadius: 4, backgroundColor: alpha(theme.palette.grey[300], 0.3), '& .MuiLinearProgress-bar': { background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`, borderRadius: 4 } }} />
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>{Math.round(overallProgress.progressPercentage)}% of all deliverables completed</Typography>
            </Box>
          )}

          {organizedVideos.length === 0 ? (
            <Alert severity="info" icon={<MovieCreationIcon />}>
              <Typography variant="body2">Video deliverables will appear here once the campaign launches.</Typography>
            </Alert>
          ) : (
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5, color: 'text.primary', display: 'flex', alignItems: 'center', gap: 1 }}>
                <VideoLibraryIcon color="primary" fontSize="small" />Video Assignments ({organizedVideos.length})
              </Typography>
              <Stack spacing={0}>
                {organizedVideos.map((video) => (
                  <DeliverableVideoCard key={video.id} videoData={video} crosspostData={video.crossposts} onLinkChange={onLinkChange} onCrossLinkChange={onCrossLinkChange} onSubmissionLinkChange={onSubmissionLinkChange} onSave={() => handleSaveSingleVideo(video.id)} isSaving={isUpdatingFields} hasUnsavedChanges={unsavedVideoIds.has(video.id)} canEdit={canEdit} theme={theme} creator={creator} />
                ))}
              </Stack>
            </Box>
          )}

          {canEdit && !creator?.assetsSent && (
            <Box sx={{ mt: 2, p: 1.5, bgcolor: alpha(theme.palette.warning.main, 0.05), borderRadius: 2, border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}` }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'warning.dark', mb: 1 }}>📋 Campaign Assets</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>Confirm when you've sent all campaign materials to the brand.</Typography>
              <Button variant="outlined" color="warning" size="small" onClick={handleAssetsSent} disabled={isUpdatingFields} startIcon={<CheckCircleIcon />} fullWidth>Confirm Assets Sent</Button>
            </Box>
          )}

          {timelineEvents && timelineEvents.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <CreatorTimeline timelineEvents={timelineEvents} onEditField={OnEditTimelineField} onSaveChanges={onSaveChanges} isSavingChanges={isUpdatingTimeline} canEdit={canEdit} openAlert={openAlert} closeDialog={closeDialog} />
            </Box>
          )}
        </InfoSection>
      </Box>
    </Box>
  );

  if (isLoading || isChecking) return <Backdrop open={true}><CircularProgress color="inherit" /></Backdrop>;

  return (
    <>
      <MainContainer>
        <PageTitle title={`Campaign Chat - ${campaign?.name} - Blitz`} />

        {/* Desktop */}
        <DesktopChatContainer>
          <DesktopSidebar elevation={2}>{renderCampaignInfo()}</DesktopSidebar>
          <DesktopChatCard elevation={2}>
            <Box sx={{ p: 2, pb: 1, borderBottom: 1, borderColor: "divider", flexShrink: 0 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography variant="body2" color="text.secondary">Discuss campaign details, deliverables, and coordination with the brand team</Typography>
                <Tooltip title="Use this chat to communicate directly with the brand about any campaign questions or updates" arrow>
                  <HelpOutlineIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                </Tooltip>
              </Box>
            </Box>
            <ChatContentWrapper>
              {conversationError && <Alert severity="error" sx={{ m: 2, flexShrink: 0 }}>{conversationError?.response?.data?.error || conversationError.message}</Alert>}
              <CampaignChatScreen ref={chatRef} conversationId={convId} campaignId={campaignId} creatorId={creatorToken.creator_user.username} sx={{ flex: 1, height: "100%", minHeight: 0, display: "flex", flexDirection: "column", overflow: "hidden" }} showWarning={false} margin={2} chatSpacing={1} onDeliverablesUpdate={(list) => setIneligibleCount(list.length)} />
            </ChatContentWrapper>
          </DesktopChatCard>
        </DesktopChatContainer>

        {/* Mobile */}
        <MobileChatContainer>
          {!mobileChatFullscreen ? (
            <MobileScrollableContent>
              <MobileCampaignAccordion expanded={mobileActiveAccordion === 'details'} onChange={() => handleMobileAccordionChange('details')} disableGutters>
                <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ backgroundColor: theme.palette.grey[50], borderBottom: `1px solid ${theme.palette.divider}`, minHeight: 56, "&.Mui-expanded": { minHeight: 56 } }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, flex: 1 }}>
                    <InfoIcon color="primary" />
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="subtitle1" fontWeight="bold">Campaign Details</Typography>
                      <Typography variant="caption" color="text.secondary">{campaign?.name} • ${creator?.agencyRate || creator?.price || "N/A"}</Typography>
                    </Box>
                    {ineligibleCount > 0 && <Badge badgeContent={ineligibleCount} color="error" sx={{ mr: 1 }}><AssignmentTurnedInIcon color="action" /></Badge>}
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ p: 0, maxHeight: 'calc(var(--vh, 1vh) * 100 - 112px)', overflowY: 'auto', overflowX: 'hidden', WebkitOverflowScrolling: 'touch', '&::-webkit-scrollbar': { width: 4 }, '&::-webkit-scrollbar-track': { backgroundColor: 'transparent' }, '&::-webkit-scrollbar-thumb': { backgroundColor: alpha(theme.palette.text.primary, 0.2), borderRadius: 2 } }}>
                  {renderCampaignInfo()}
                </AccordionDetails>
              </MobileCampaignAccordion>
              <MobileCampaignAccordion expanded={false} disableGutters>
                <AccordionSummary onClick={(e) => { e.preventDefault(); handleMobileAccordionChange('chat'); }} sx={{ backgroundColor: theme.palette.grey[50], borderBottom: `1px solid ${theme.palette.divider}`, minHeight: 56, "&.Mui-expanded": { minHeight: 56 }, cursor: 'pointer' }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <ChatIcon color="primary" />
                    <Box>
                      <Typography variant="subtitle1" fontWeight="bold">Campaign Chat</Typography>
                      <Typography variant="caption" color="text.secondary">Tap to open chat</Typography>
                    </Box>
                  </Box>
                </AccordionSummary>
              </MobileCampaignAccordion>
            </MobileScrollableContent>
          ) : (
            <MobileFullscreenChat>
              <MobileChatHeader>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ChatIcon color="primary" />
                  <Box>
                    <Typography variant="subtitle1" fontWeight="bold">Campaign Chat</Typography>
                    <Typography variant="caption" color="text.secondary">{campaign?.name}</Typography>
                  </Box>
                </Box>
                <IconButton onClick={() => handleMobileChatToggle(false)} edge="end"><CloseIcon /></IconButton>
              </MobileChatHeader>
              {conversationError && <Alert severity="error" sx={{ m: 1, flexShrink: 0 }}>{conversationError?.response?.data?.error || conversationError.message}</Alert>}
              <CampaignChatScreen ref={chatRef} conversationId={convId} campaignId={campaignId} creatorId={creatorToken.creator_user.username} sx={{ flex: 1, height: "100%", minHeight: 0, display: "flex", flexDirection: "column", overflow: "hidden" }} showWarning={false} margin={1} chatSpacing={1} onDeliverablesUpdate={(list) => setIneligibleCount(list.length)} />
            </MobileFullscreenChat>
          )}
        </MobileChatContainer>
      </MainContainer>
      <AlertDialog alertState={dialogState} />
    </>
  );
};

export default CreatorSpecificCampaign;